import { useMemo } from 'react';
import { Box, Flex, IconButton, Popover, PopoverBody, PopoverContent, PopoverTrigger, Tag, Text, VStack, Wrap, WrapItem, useColorMode } from '@chakra-ui/react';
import { CloseIcon } from '@chakra-ui/icons';
import { useTranslation } from 'react-i18next';
import { DEFAULT_CATEGORY_COLOR, FALLBACK_CATEGORY_COLOR } from './CategoriesPage';
import CategoryTimelineChart, { SHORT_MONTHS } from './CategoryTimelineChart';
import CategoryKeyMetrics, { CurrencyLabel } from './CategoryKeyMetrics';
import Figure from '../shared/Figure';
import { categoryTint } from '../../utils/categoryTint';

// The overview's bars: the dashboard's income and spending chart colours.
const TYPE_BAR_COLOR = {
  income: 'redesign.accent.green',
  expenses: 'redesign.accent.expense',
};
const DEFAULT_TYPE_BAR_COLOR = 'redesign.text.muted';
const PILL_LIMIT = 5;

// Here they colour figures, not bars, so they take the text-safe shades.
const SEMAPHORE_GREEN = 'redesign.amount.income';
const SEMAPHORE_AMBER = 'redesign.text.warn';
const SEMAPHORE_RED = 'redesign.accent.red';

function getSemaphoreColor(ratio) {
  if (ratio >= 1) return SEMAPHORE_RED;
  if (ratio >= 0.75) return SEMAPHORE_AMBER;
  return SEMAPHORE_GREEN;
}

function CategoryPill({ category, onClick }) {
  const color = category.color || DEFAULT_CATEGORY_COLOR;
  const { colorMode } = useColorMode();
  const tint = categoryTint(color, colorMode);
  return (
    <Tag
      size="sm"
      variant="subtle"
      fontSize="2xs"
      textTransform="uppercase"
      fontWeight="600"
      borderRadius="full"
      px="6px"
      py="2px"
      letterSpacing="0.03em"
      whiteSpace="nowrap"
      cursor={onClick ? 'pointer' : 'default'}
      _hover={onClick ? { opacity: 0.8 } : undefined}
      onClick={onClick}
      bg={tint.bg}
      color={tint.fg}
    >
      {category.icon ? `${category.icon} ${category.name}` : category.name}
    </Tag>
  );
}

function ExtraCategoriesPopover({ categories, onCategorySelect }) {
  const { colorMode } = useColorMode();
  return (
    <Popover placement="bottom-start" isLazy>
      {({ onClose }) => (
        <>
          <PopoverTrigger>
            <Tag
              as="button"
              size="sm"
              variant="subtle"
              fontSize="2xs"
              fontWeight="600"
              borderRadius="full"
              px="8px"
              py="2px"
              cursor="pointer"
              _hover={{ bg: 'redesign.bg.hover' }}
              bg="redesign.bg.card"
              color="redesign.text.muted"
            >
              +{categories.length}
            </Tag>
          </PopoverTrigger>
          <PopoverContent w="220px">
            <PopoverBody p={1}>
              <VStack spacing={0} align="stretch">
                {categories.map((c) => {
                  const tint = categoryTint(c.color || DEFAULT_CATEGORY_COLOR, colorMode);
                  return (
                    <Flex
                      key={c.id}
                      as="button"
                      align="center"
                      gap={2}
                      px={2}
                      py={1.5}
                      borderRadius="md"
                      _hover={{ bg: 'redesign.bg.hover' }}
                      onClick={() => {
                        onClose();
                        if (onCategorySelect) onCategorySelect(c.id);
                      }}
                      textAlign="left"
                    >
                      <Text fontSize="md" w="20px" textAlign="center">{c.icon || '📊'}</Text>
                      {c.count > 0 && (
                        <Box
                          px="6px"
                          py="2px"
                          borderRadius="md"
                          bg={tint.bg}
                          minW="20px"
                          textAlign="center"
                        >
                          <Text fontSize="2xs" fontWeight="600" color={tint.fg}>{c.count}</Text>
                        </Box>
                      )}
                      <Text fontSize="sm" color="redesign.heading" flex={1} noOfLines={1}>{c.name}</Text>
                    </Flex>
                  );
                })}
              </VStack>
            </PopoverBody>
          </PopoverContent>
        </>
      )}
    </Popover>
  );
}

function CategoryBudgetRow({ row, onClick, isIncome = false }) {
  const color = row.color || FALLBACK_CATEGORY_COLOR;
  const hasAvg = row.sixMonthAvg > 0;
  const left = row.sixMonthAvg - row.spent;
  const ratio = hasAvg ? row.spent / row.sixMonthAvg : 0;
  // For expenses: spending less than avg is good (green); over is bad (red).
  // For income: earning more than avg is good (green); under is bad (red).
  const overShoot = isIncome ? left > 0 : left < 0;
  const leftColor = !hasAvg
    ? 'redesign.text.faint'
    : overShoot
      ? SEMAPHORE_RED
      : getSemaphoreColor(isIncome ? 1 / Math.max(ratio, 0.0001) : ratio);

  return (
    <Flex
      as="button"
      align="center"
      w="100%"
      px={2}
      py={2}
      borderRadius="md"
      _hover={{ bg: 'redesign.bg.hover' }}
      onClick={() => onClick(row)}
      textAlign="left"
      gap={2}
    >
      <Box w="6px" h="6px" borderRadius="full" bg={color} flexShrink={0} />
      <Text fontSize="md" w="20px" textAlign="center" flexShrink={0}>{row.icon || '📊'}</Text>
      <Text fontSize="sm" color="redesign.heading" flex={1} minW={0} noOfLines={1} isTruncated>{row.name}</Text>
      <Text fontSize="sm" fontWeight="600" color="redesign.heading" w="105px" textAlign="right" whiteSpace="nowrap" flexShrink={0}>
        <CurrencyLabel currency={row.currency} />
        {row.spent.toLocaleString('en-US', { maximumFractionDigits: 0 })}
      </Text>
      <Text fontSize="sm" fontWeight="500" color="redesign.text.muted" w="105px" textAlign="right" whiteSpace="nowrap" flexShrink={0}>
        {hasAvg ? (
          <>
            <CurrencyLabel currency={row.currency} />
            {row.sixMonthAvg.toLocaleString('en-US', { maximumFractionDigits: 0 })}
          </>
        ) : (
          <Text as="span" color="redesign.text.faint">—</Text>
        )}
      </Text>
      <Text fontSize="sm" fontWeight="600" color={leftColor} w="105px" textAlign="right" whiteSpace="nowrap" flexShrink={0}>
        {hasAvg ? (
          <>
            <CurrencyLabel currency={row.currency} />
            {overShoot && '−'}{Math.abs(left).toLocaleString('en-US', { maximumFractionDigits: 0 })}
          </>
        ) : (
          <Text as="span" color="redesign.text.faint">—</Text>
        )}
      </Text>
    </Flex>
  );
}

function HeaderTotals({ selectedMonthLabel, totalsByCurrency, budgetByCurrency, typeFilter }) {
  const { t } = useTranslation('categories');
  const currencies = Object.keys({ ...totalsByCurrency, ...budgetByCurrency }).sort();
  const isIncome = typeFilter === 'income';
  const headerLabel = isIncome
    ? t('summary.earnedIn', { month: selectedMonthLabel })
    : t('summary.spentIn', { month: selectedMonthLabel });

  if (currencies.length === 0) {
    return (
      <Box textAlign="right">
        <Text textStyle="microLabel" mb={1}>
          {headerLabel}
        </Text>
        <Figure size="md" code="$" amount="0" display="block" />
      </Box>
    );
  }

  const isMulti = currencies.length > 1;

  return (
    <Box textAlign="right">
      <Text textStyle="microLabel" mb={1} pr={isMulti ? '34px' : 0}>
        {headerLabel}
      </Text>
      {currencies.map((c) => {
        const spent = totalsByCurrency[c] || 0;
        const budget = budgetByCurrency[c] || 0;
        const left = budget - spent;
        const deltaPct = budget > 0 ? Math.round((spent / budget - 1) * 100) : null;
        // For expenses, going over avg is bad (red); for income, it's good (green).
        const isOverGood = isIncome ? deltaPct > 0 : deltaPct < 0;
        const deltaColor = deltaPct == null || deltaPct === 0
          ? 'redesign.text.faint'
          : isOverGood ? SEMAPHORE_GREEN : SEMAPHORE_RED;
        const overShoot = isIncome ? left > 0 : left < 0;
        const leftColor = budget === 0
          ? 'redesign.text.faint'
          : overShoot
            ? SEMAPHORE_RED
            : getSemaphoreColor(isIncome ? budget / Math.max(spent, 1) : spent / budget);
        const overWord = isIncome ? t('allCategories.diff') : t('allCategories.diff');
        const underWord = isIncome ? t('allCategories.left') : t('allCategories.left');
        return (
          <Box key={c} mb={1}>
            <Flex justify="flex-end" align="baseline">
              <Figure
                size={isMulti ? 'sm' : 'md'}
                code={c}
                amount={spent.toLocaleString('en-US', { maximumFractionDigits: 0 })}
              />
              {isMulti && (
                <Box w="34px" pl={1.5} textAlign="left">
                  {deltaPct !== null && deltaPct !== 0 && (
                    <Text fontSize="2xs" fontWeight="600" color={deltaColor}>
                      {Math.abs(deltaPct)}%
                    </Text>
                  )}
                </Box>
              )}
            </Flex>
            {!isMulti && budget > 0 && (
              <Flex justify="flex-end" align="baseline" gap={1}>
                {deltaPct !== 0 && (
                  <Text fontSize="2xs" fontWeight="600" color={deltaColor}>
                    {deltaPct > 0 ? '+' : ''}{deltaPct}%
                  </Text>
                )}
                <Text fontSize="xs" fontWeight="500" color={leftColor}>
                  <CurrencyLabel currency={c} fontSize="2xs" />
                  {Math.abs(left).toLocaleString('en-US', { maximumFractionDigits: 0 })} {left < 0 ? overWord : underWord}
                </Text>
              </Flex>
            )}
          </Box>
        );
      })}
    </Box>
  );
}

export default function AllCategoriesDetailPanel({
  categories,
  monthlyTimeline,
  yearlyMetrics,
  sixMoAvgByCurrency,
  sixMoAvgByCatCurrency,
  currentMonthName,
  typeFilter,
  selectedMonthIdx,
  currentMonthIdx,
  onSelectMonth,
  onClose,
  onCategorySelect,
}) {
  const { t } = useTranslation('categories');

  const selectedMonth = monthlyTimeline?.[selectedMonthIdx] || null;
  const selectedMonthLabel = selectedMonth
    ? `${SHORT_MONTHS[selectedMonth.month - 1]} ${selectedMonth.year}`
    : currentMonthName;

  const typeColor = TYPE_BAR_COLOR[typeFilter] || DEFAULT_TYPE_BAR_COLOR;

  // Per-currency totals for the selected month
  const selectedMonthTotalsByCurrency = useMemo(() => {
    if (!selectedMonth) return {};
    const totals = {};
    selectedMonth.data.forEach((d) => {
      const c = d.currency || 'COP';
      totals[c] = (totals[c] || 0) + Math.abs(d.total || 0);
    });
    return totals;
  }, [selectedMonth]);

  // Per-(category, currency) rows for the selected month
  const catById = useMemo(
    () => Object.fromEntries(categories.map((c) => [c.id, c])),
    [categories]
  );
  const selectedMonthRows = useMemo(() => {
    if (!selectedMonth) return [];
    const byKey = {};
    selectedMonth.data.forEach((d) => {
      if (d.category_id == null) return;
      const c = d.currency || 'COP';
      const key = `${d.category_id}::${c}`;
      if (!byKey[key]) byKey[key] = { category_id: d.category_id, currency: c, spent: 0, count: 0 };
      byKey[key].spent += Math.abs(d.total || 0);
      byKey[key].count += d.num_movements || 0;
    });
    return Object.values(byKey)
      .map((r) => {
        const cat = catById[r.category_id];
        if (!cat) return null;
        return {
          ...cat,
          currency: r.currency,
          spent: r.spent,
          count: r.count,
          sixMonthAvg: sixMoAvgByCatCurrency?.[`${cat.id}::${r.currency}`] || 0,
        };
      })
      .filter(Boolean)
      .sort((a, b) => b.spent - a.spent);
  }, [selectedMonth, catById, sixMoAvgByCatCurrency]);

  const rowsByCurrency = useMemo(() => {
    const groups = {};
    selectedMonthRows.forEach((r) => {
      const c = r.currency || 'COP';
      if (!groups[c]) groups[c] = [];
      groups[c].push(r);
    });
    return Object.entries(groups)
      .map(([currency, rows]) => ({
        currency,
        rows,
        total: rows.reduce((s, r) => s + r.spent, 0),
      }))
      .sort((a, b) => b.total - a.total);
  }, [selectedMonthRows]);

  // Top categories for pills — ranked by selected-month spending across currencies.
  // Cross-currency sums aren't meaningful as totals, but they're fine for ranking.
  const topCategories = useMemo(() => {
    const merged = {};
    selectedMonthRows.forEach((r) => {
      if (!merged[r.id]) {
        merged[r.id] = { id: r.id, name: r.name, icon: r.icon, color: r.color, spent: 0, count: 0 };
      }
      merged[r.id].spent += r.spent;
      merged[r.id].count += r.count || 0;
    });
    return Object.values(merged).sort((a, b) => b.spent - a.spent);
  }, [selectedMonthRows]);

  const visiblePills = topCategories.slice(0, PILL_LIMIT);
  const extraCategories = topCategories.slice(PILL_LIMIT);

  const handleCategoryClick = (row) => {
    if (onCategorySelect) onCategorySelect(row.id);
  };

  return (
    <Box h="100%" bg="redesign.bg.surface" display="flex" flexDirection="column">
      {/* Header */}
      <Flex
        justify="space-between"
        align="center"
        px={{ base: 3, md: 5 }}
        py={3}
        borderBottom="1px solid"
        borderColor="redesign.border"
        flexShrink={0}
      >
        <Text fontSize="sm" fontWeight="500" color="redesign.text">{t('allCategories.overview')}</Text>
        <IconButton
          icon={<CloseIcon boxSize={2.5} />}
          size="sm"
          variant="ghost"
          onClick={onClose}
          aria-label={t('allCategories.close')}
        />
      </Flex>

      <Box flex={1} overflowY="auto">
        {/* Identity + totals */}
        <Box px={5} pt={5} pb={4}>
          <Flex align="flex-start" justify="space-between" gap={4} mb={3}>
            <Box flex={1} minW={0}>
              <Text fontSize="xl" fontWeight="bold" color="redesign.heading" mb={2}>
                {t('allCategories.allRegular')}
              </Text>
              {visiblePills.length > 0 && (
                <Wrap spacing={1.5}>
                  {visiblePills.map((c) => (
                    <WrapItem key={c.id}>
                      <CategoryPill category={c} onClick={() => onCategorySelect && onCategorySelect(c.id)} />
                    </WrapItem>
                  ))}
                  {extraCategories.length > 0 && (
                    <WrapItem>
                      <ExtraCategoriesPopover
                        categories={extraCategories}
                        onCategorySelect={onCategorySelect}
                      />
                    </WrapItem>
                  )}
                </Wrap>
              )}
            </Box>
            <HeaderTotals
              selectedMonthLabel={selectedMonthLabel}
              totalsByCurrency={selectedMonthTotalsByCurrency}
              budgetByCurrency={sixMoAvgByCurrency || {}}
              typeFilter={typeFilter}
            />
          </Flex>

          {/* Timeline */}
          <Box mt={4} mb={1}>
            <CategoryTimelineChart
              monthlyTimeline={monthlyTimeline}
              categoryId={null}
              color={typeColor}
              selectedIdx={selectedMonthIdx}
              onSelect={onSelectMonth}
              currentMonthIdx={currentMonthIdx}
            />
          </Box>
        </Box>

        {/* Key metrics */}
        <Box px={5} py={4} borderTop="1px solid" borderColor="redesign.border">
          <CategoryKeyMetrics yearlyMetrics={yearlyMetrics} categoryId={null} typeFilter={typeFilter} />
        </Box>

        {/* Categories list */}
        <Box px={5} py={4} borderTop="1px solid" borderColor="redesign.border">
          <Flex align="center" mb={2} px={2} gap={2}>
            <Text textStyle="microLabel" flex={1}>
              {t('allCategories.categories')}
            </Text>
            <Text textStyle="microLabel" w="105px" textAlign="right">
              {typeFilter === 'income' ? t('allCategories.earned') : t('allCategories.spent')}
            </Text>
            <Text textStyle="microLabel" w="105px" textAlign="right">
              {t('allCategories.average')}
            </Text>
            <Text textStyle="microLabel" w="105px" textAlign="right">
              {typeFilter === 'income' ? t('allCategories.diff') : t('allCategories.left')}
            </Text>
          </Flex>

          {rowsByCurrency.length === 0 ? (
            <Text px={2} py={4} fontSize="sm" color="redesign.text.muted">
              {t('allCategories.noActivity', { month: selectedMonthLabel })}
            </Text>
          ) : rowsByCurrency.length === 1 ? (
            <VStack spacing={0} align="stretch">
              {rowsByCurrency[0].rows.map((row) => (
                <CategoryBudgetRow
                  key={`${row.id}::${row.currency}`}
                  row={row}
                  onClick={handleCategoryClick}
                  isIncome={typeFilter === 'income'}
                />
              ))}
            </VStack>
          ) : (
            <VStack spacing={3} align="stretch">
              {rowsByCurrency.map(({ currency, rows }) => (
                <Box key={currency}>
                  <Flex align="center" gap={2} mb={1}>
                    <Text fontSize="2xs" fontWeight={600} color="redesign.text.muted" bg="redesign.bg.card" px={2} py="2px" borderRadius="md">
                      {currency}
                    </Text>
                    <Box flex={1} h="1px" bg="redesign.border" />
                  </Flex>
                  <VStack spacing={0} align="stretch">
                    {rows.map((row) => (
                      <CategoryBudgetRow
                        key={`${row.id}::${row.currency}`}
                        row={row}
                        onClick={handleCategoryClick}
                        isIncome={typeFilter === 'income'}
                      />
                    ))}
                  </VStack>
                </Box>
              ))}
            </VStack>
          )}
        </Box>
      </Box>
    </Box>
  );
}
